import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  tags = [],
  siteName = 'Emmanuel Tete Boye Portfolio'
}) => {
  const siteUrl = 'https://tboyetech-portfolio.netlify.app';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;

  // Use different fallback images for different content types
  let defaultImage = '/images/image1.jpg'; // Portfolio default
  if (type === 'article') {
    defaultImage = '/images/blog-default.jpg'; // Blog default - you can create this
  }

  const fullImageUrl = image?.startsWith('http') ? image : `${siteUrl}${image || defaultImage}`;

  // Construct full title
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Software/AI Engineer`;

  // Default description
  const defaultDescription = 'Software/AI Engineer from Ghana specializing in React, Python, AI integrations, and modern web technologies.';
  const seoDescription = description || defaultDescription;

  // Create structured data for article
  const structuredData = type === 'article' ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": seoDescription,
    "image": fullImageUrl,
    "url": fullUrl,
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    "author": {
      "@type": "Person",
      "name": author || "Emmanuel Tete Boye",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Person",
      "name": "Emmanuel Tete Boye",
      "url": siteUrl
    },
    "keywords": tags.join(', '),
    "articleSection": tags[0] || "Technology",
    "inLanguage": "en-US"
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={fullUrl} />

      {/* OpenGraph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title || fullTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || 'Emmanuel Tete Boye Portfolio'} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Article Specific Tags */}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && tags.length > 0 && tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tboyetech" />
      <meta name="twitter:creator" content="@tboyetech" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title || fullTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={title || 'Emmanuel Tete Boye Portfolio'} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
